import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";

import { EvaService } from "src/app/core/services/eva.service";
import { EvaConfirmService } from "src/app/shared/ds/eva-confirm-dialog/eva-confirm.service";
import { EvaPopoverComponent } from "src/app/shared/ds/eva-popover/eva-popover.component";
import { NoteContextService } from "src/app/services/note-context.service";
import { EvaCompanyService } from "src/app/features/eva-company/services/evaCompany.service";

import { AiComposerComponent, ComposedMessage } from "../ai-composer/ai-composer.component";
import { AiThreadComponent, MessageView } from "../ai-thread/ai-thread.component";
import { pickGreeting } from "./ai-greetings";
import {
  AiChatError,
  AiChatService,
  ConversationSummary,
  PendingAction,
  conversationCost,
} from "../services/ai-chat.service";
import { AiDrawerSession } from "../services/ai-drawer-session.service";

/**
 * Chat bleu de l'assistant : en tête de l'accueil (`home`), dans le galet Tâches / Notes (`drawer`)
 * et en pleine page sur /ai (`page`) : même interface, même conversation.
 * L'accueil ne fait que lancer : la question part dans le galet. La conversation du galet vit dans
 * `AiDrawerSession` : fermer le galet ou changer de page ne la coupe pas.
 * « Agrandir » rouvre la même conversation dans la page /ai.
 */
@Component({
  selector: "app-ai-home-chat",
  standalone: true,
  imports: [CommonModule, EvaPopoverComponent, AiComposerComponent, AiThreadComponent],
  templateUrl: "./ai-home-chat.component.html",
  styleUrls: ["./ai-home-chat.component.scss"],
})
export class AiHomeChatComponent implements OnInit {
  private readonly chat = inject(AiChatService);
  private readonly session = inject(AiDrawerSession);
  private readonly evaService = inject(EvaService);
  private readonly evaCompanyService = inject(EvaCompanyService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly noteContext = inject(NoteContextService);
  private readonly confirm = inject(EvaConfirmService);
  // Le galet Tâches / Notes est OnPush : chaque mise à jour asynchrone doit le marquer.
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() variant: "home" | "drawer" | "page" = "home";
  /** Galet : fermeture demandée par la croix de l'en-tête. */
  @Output() closed = new EventEmitter<void>();

  @ViewChild(AiThreadComponent) private thread?: AiThreadComponent;
  @ViewChild(AiComposerComponent) private composer?: AiComposerComponent;

  readonly greeting = pickGreeting(new Date(), this.evaCompanyService.getCurrentEvaEmployee().first_name ?? "");

  newModel: string | null = null;
  showHistory = false;
  loadingHistory = false;
  conversations: ConversationSummary[] = [];

  get conversationId(): string | null {
    return this.session.conversationId;
  }
  get conversationModel(): string | null {
    return this.session.model;
  }
  get messages(): MessageView[] {
    return this.session.messages;
  }
  get pending(): PendingAction | null {
    return this.session.pending;
  }
  get busy(): boolean {
    return this.session.busy();
  }
  get progress(): string {
    return this.session.progress();
  }

  /** Galet et page /ai affichent la conversation ; l'accueil garde son bloc compact. */
  get active(): boolean {
    return this.variant !== "home";
  }

  /** Accueil : une conversation du galet peut être reprise. */
  get resumable(): boolean {
    return this.variant === "home" && !!this.session.conversationId;
  }

  ngOnInit(): void {
    let wasBusy = this.busy;
    this.session.changed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // Réponse terminée : la liste se relit (titre de la conversation, coût).
      if (wasBusy && !this.busy && this.showHistory) this.loadHistory();
      wasBusy = this.busy;
      this.scrollToBottom();
      this.cdr.markForCheck();
    });
    if (this.variant === "home") return;
    // Page /ai : l'historique est ouvert d'emblée, comme une colonne.
    if (this.variant === "page") this.toggleHistory();
    this.session.viewers++;
    this.session.unread.set(false);
    this.destroyRef.onDestroy(() => this.session.viewers--);
    // Réouverture hors tour en cours : l'agent fait foi (la conversation a pu continuer dans /ai).
    if (!this.session.busy() && this.session.conversationId) this.session.open(this.session.conversationId);
    else setTimeout(() => this.scrollToBottom());
  }

  resume(): void {
    this.noteContext.openDrawerAssistant();
  }

  reset(): void {
    this.session.reset();
    this.showHistory = false;
    this.conversations = [];
  }

  send(message: ComposedMessage): void {
    if (this.busy) return;
    // Accueil : nouvelle conversation, suivie dans le galet.
    if (this.variant === "home") {
      this.session.reset();
      this.noteContext.openDrawerAssistant();
    }
    // Vidé seulement une fois la conversation prête : un échec ne perd pas la question.
    this.session.send(message, this.newModel, () => this.composer?.clear());
  }

  stop(): void {
    this.session.stop();
  }

  decide(action: "valider" | "refuser"): void {
    this.session.decide(action);
  }

  async toggleHistory(): Promise<void> {
    this.showHistory = !this.showHistory;
    if (!this.showHistory) return;
    this.loadingHistory = true;
    await this.loadHistory();
  }

  private async loadHistory(): Promise<void> {
    try {
      this.conversations = await this.chat.conversations();
    } catch (e) {
      this.fail(e);
    } finally {
      this.loadingHistory = false;
      this.cdr.markForCheck();
    }
  }

  cost(conversation: ConversationSummary): string {
    return conversationCost(conversation.cout_chf);
  }

  remove(id: string): void {
    this.confirm.confirm({
      header: "Supprimer la conversation",
      message: "La conversation et son historique seront définitivement supprimés.",
      acceptLabel: "Supprimer",
      severity: "danger",
      accept: async () => {
        try {
          await this.chat.deleteConversation(id);
          this.conversations = this.conversations.filter((c) => c.id !== id);
          this.session.forget(id);
        } catch (e) {
          this.fail(e);
        } finally {
          this.cdr.markForCheck();
        }
      },
    });
  }

  open(id: string): void {
    this.session.open(id);
  }

  /** Conversation de la liste qui réfléchit encore (en arrière-plan ou affichée). */
  isBusy(id: string): boolean {
    return this.session.isBusy(id);
  }

  openFullView(): void {
    // Le galet se referme : la conversation continue dans /ai, « Réduire » l'y ramène.
    if (this.variant === "drawer") this.closed.emit();
    this.router.navigate(["/ai"], this.conversationId ? { queryParams: { conversation: this.conversationId } } : {});
  }

  private fail(e: unknown): void {
    const message = e instanceof AiChatError ? e.message : "L'assistant ne répond pas.";
    this.evaService.showError(message, "Assistant", e instanceof AiChatError ? e.errorType : String(e));
  }

  private scrollToBottom(): void {
    this.thread?.scrollToBottom();
  }
}
